import { CheckCircle2 } from 'lucide-react';

interface WizardStep {
  label: string;
  description?: string;
}

interface WizardStepIndicatorProps {
  steps: WizardStep[];
  currentStep: number;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  className?: string;
}

export function WizardStepIndicator({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  className = '',
}: WizardStepIndicatorProps) {
  const isCompleted = (index: number) =>
    completedSteps ? completedSteps.includes(index) : index < currentStep;

  const isClickable = (index: number) =>
    !!onStepClick && (isCompleted(index) || index === currentStep);

  return (
    <nav
      aria-label="Wizard progress"
      className={`flex items-start gap-0 ${className}`}
    >
      {steps.map((step, idx) => {
        const completed = isCompleted(idx);
        const current = idx === currentStep;
        const clickable = isClickable(idx);

        return (
          <div key={idx} className="flex items-start flex-1 min-w-0">
            {/* Connector line (before step, skip first) */}
            {idx > 0 && (
              <div className="flex-shrink-0 mt-4 w-8 sm:w-12">
                <div
                  className={`h-0.5 w-full ${
                    completed || current ? 'bg-primary-400' : 'bg-surface-300'
                  }`}
                />
              </div>
            )}

            {/* Step circle + label */}
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick?.(idx)}
              className={`flex flex-col items-center gap-1.5 min-w-0 ${
                clickable ? 'cursor-pointer' : 'cursor-default'
              }`}
              aria-current={current ? 'step' : undefined}
            >
              {/* Circle */}
              <div
                className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                  completed
                    ? 'bg-primary-600 text-white'
                    : current
                      ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-600'
                      : 'bg-surface-200 text-surface-500'
                }`}
              >
                {completed ? (
                  <CheckCircle2 className="w-4.5 h-4.5" />
                ) : (
                  idx + 1
                )}
              </div>

              {/* Label + description */}
              <div className="text-center min-w-0">
                <p
                  className={`text-xs font-medium leading-tight truncate ${
                    current
                      ? 'text-primary-700'
                      : completed
                        ? 'text-primary-600'
                        : 'text-surface-500'
                  }`}
                >
                  {step.label}
                </p>
                {step.description && (
                  <p className="text-xs text-surface-400 leading-tight mt-0.5 hidden sm:block">
                    {step.description}
                  </p>
                )}
              </div>
            </button>
          </div>
        );
      })}
    </nav>
  );
}
